import { useMsal } from "@azure/msal-react";
import { DirectionalHint, IconButton, IContextualMenuItem, Modal, Text, useTheme } from "@fluentui/react";
import { RootState } from "app/store";
import { IGridItem } from "components/controls/CustomDetailList/IGridItem";
import InformationCallout from "components/controls/InformationCallout";
import LookupControlBase from "components/controls/LookupControlBase";
import LookupControlDialog, { TSearchPredicateAsync } from "components/controls/LookupControlDialog";
import { AppLinkToExternal, appStyles } from "global/Application";
import { AppDataSource, IAuditAttribute, IAuditFieldValueUserID, IAuditResponseAuditDetails } from "global/DataSources/AppDataSource";
import { MsalTokenSource } from "global/DataSources/MsalTokenSource";
import Formatters from "global/Utility/Formatters";
import { ArrayToLookupByPredicate } from "global/Utility/TSFunctions";
import {
    IEntityReferenceValue as FAM_IEntityReferenceValue,
    IObjectWithNameId as FAM_IObjectWithNameId
} from "model/FlexibleAttributeModel";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";

interface IProps {
    onPick?: (snapshotRef: FAM_IEntityReferenceValue | null, snapshotVersion?: string) => void;
    onCancel?: () => void;
    searchOnMount?: boolean;
    searchVersion?: boolean;
    valueSnapshot?: FAM_IEntityReferenceValue;
    valueSnapshotVersion?: string;
    defaultValueAccount?: FAM_IEntityReferenceValue;
    valueAccount?: FAM_IEntityReferenceValue;
    defaultValueSnapshot?: FAM_IEntityReferenceValue;
    defaultValueSnapshotVersion?: string;
    defaultValueViewKey?: TViewKey;
    label?: string;
    disabled?: boolean;
}


type TViewKey =
    'effectiveDateGoldStandardView' |
    'expandedView' |
    'inProgressAndGoldStandardView' |
    'associationTemplateView' |
    'allView' |
    'inactiveView' |
    'blankView';


interface IAuditData {
    data: IAuditResponseAuditDetails,
    user: IAuditFieldValueUserID,
    compare: Record<string, {
        oldAudit?: IAuditAttribute | undefined;
        newAudit?: IAuditAttribute | undefined;
    }>,
    compareKeys: string,
}

interface IScopeSearchResult extends FAM_IObjectWithNameId {
    version: string,
    audit: IAuditData,
}

type TCompareAuditAttribute = Record<
    string,
    {
        oldAudit?: IAuditAttribute | undefined;
        newAudit?: IAuditAttribute | undefined;
    }
>;


type TRecordIAuditAttribute = Record<string, IAuditAttribute>;


const SnapshotLookup: React.FC<IProps> = (props) => {
    const [searchAccountState, setSearchAccount] = useState<FAM_IEntityReferenceValue | null>(props.valueAccount ?? props.defaultValueAccount ?? null);
    const [searchSnapshotState, setSearchSnapshot] = useState<FAM_IEntityReferenceValue | null>(props.valueSnapshot ?? props.defaultValueSnapshot ?? null);
    const [searchSnapshotVersion, setSearchSnapshotVersion] = useState<string | null>(props.valueSnapshotVersion ?? props.defaultValueSnapshotVersion ?? null);

    const searchAccount = props.valueAccount ?? searchAccountState;
    const searchSnapshot = props.valueSnapshot ?? searchSnapshotState;

    const theme = useTheme();

    const { instance } = useMsal();
    const state = useSelector((state: RootState) => state);
    const tokenSource = new MsalTokenSource(instance);
    const dataSource = new AppDataSource(tokenSource);

    const [searching, setSearching] = useState(!!props.searchOnMount);

    const [isOpenModal, setIsOpenModal] = useState(false);

    const viewSearchBlueRelationship = useCallback(() => ({
        "viewName": "searchBlueRelationship",
        "entityName": "account",
        "entitySetName": "accounts",
        "fetchXML": `
                <fetch version="1.0" output-format="xml-platform" mapping="logical" distinct="true" top="200">
                    <entity name="account">
                        <attribute name="name"/>
                        <attribute name="primarycontactid"/>
                        <attribute name="accountid"/>
                        <attribute name="customertypecode" />
                        <attribute name="createdon"/>
                        <attribute name="parentaccountid" />
                        <attribute name="bcbs_partneraccountexecid" />
                        <attribute name="bcbs_partneraccountmanagerid" />
                        <attribute name="bcbs_partnerdirectorid" />
                        <order attribute="name" descending="false"/>
                        <filter type="and">
                            <condition attribute="statecode" operator="eq" value="0" />
                            <condition attribute="customertypecode" operator="eq" value="3" />
                            <condition attribute="bcbs_customerstatuscode" operator="eq" value="808900001" />
                            <condition attribute="name" operator="like" value="%SEARCHVALUE%"/>
                        </filter>
                        <link-entity name="bcbs_groupconfigurationsnapshot" alias="aa" link-type="inner" from="bcbs_groupid" to="accountid" />
                    </entity>
                </fetch>`,
        "layout": {
            "Name": "resultset",
            "Object": 1,
            "Rows": [
                {
                    "Name": "result",
                    "Id": "accountid",
                    "Cells": [
                        {
                            "Name": "customertypecode",
                            "Width": 300,
                            "RelatedEntityName": "",
                            "DisableMetaDataBinding": false,
                            "LabelId": "",
                            "IsHidden": false,
                            "DisableSorting": false,
                            "AddedBy": "",
                            "Desc": "",
                            "CellType": "",
                            "ImageProviderWebresource": "",
                            "ImageProviderFunctionName": "",
                            "DisplayName": "Account Type",
                            "IsPrimaryId": false,
                            "AttributeType": "Lookup",
                            "IsPrimaryName": true
                        },
                        {
                            "Name": "name",
                            "Width": 300,
                            "RelatedEntityName": "",
                            "DisableMetaDataBinding": false,
                            "LabelId": "",
                            "IsHidden": false,
                            "DisableSorting": false,
                            "AddedBy": "",
                            "Desc": "",
                            "CellType": "",
                            "ImageProviderWebresource": "",
                            "ImageProviderFunctionName": "",
                            "DisplayName": "Account Name",
                            "IsPrimaryId": false,
                            "AttributeType": "String",
                            "IsPrimaryName": true
                        },
                        {
                            "Name": "_parentaccountid_value",
                            "Width": 300,
                            "RelatedEntityName": "",
                            "DisableMetaDataBinding": false,
                            "LabelId": "",
                            "IsHidden": false,
                            "DisableSorting": false,
                            "AddedBy": "",
                            "Desc": "",
                            "CellType": "",
                            "ImageProviderWebresource": "",
                            "ImageProviderFunctionName": "",
                            "DisplayName": "Partner Plan",
                            "IsPrimaryId": false,
                            "AttributeType": "Lookup",
                            "IsPrimaryName": false
                        },
                        {
                            "Name": "primarycontactid",
                            "Width": 250,
                            "RelatedEntityName": "",
                            "DisableMetaDataBinding": false,
                            "LabelId": "",
                            "IsHidden": false,
                            "DisableSorting": false,
                            "AddedBy": "",
                            "Desc": "",
                            "CellType": "",
                            "ImageProviderWebresource": "",
                            "ImageProviderFunctionName": "",
                            "DisplayName": "Primary Contact",
                            "IsPrimaryId": false,
                            "AttributeType": "Lookup",
                            "IsPrimaryName": false
                        },
                        {
                            "Name": "createdon",
                            "Width": 150,
                            "RelatedEntityName": "",
                            "DisableMetaDataBinding": false,
                            "LabelId": "",
                            "IsHidden": false,
                            "DisableSorting": false,
                            "AddedBy": "",
                            "Desc": "",
                            "CellType": "",
                            "ImageProviderWebresource": "",
                            "ImageProviderFunctionName": "",
                            "DisplayName": "Created On",
                            "IsPrimaryId": false,
                            "AttributeType": "DateTime",
                            "IsPrimaryName": false
                        }
                    ],
                    "MultiObjectIdField": "",
                    "LayoutStyle": ""
                }
            ],
            "CustomControlDescriptions": [],
            "Jump": "name",
            "Select": true,
            "Icon": true,
            "Preview": true,
            "IconRenderer": ""
        }
    }), []);

    const viewSearchSnapshot = useCallback(() => ({
        "viewSearchSnapshot": "viewSearchSnapshot",
        "entityName": "bcbs_groupconfigurationsnapshot",
        "entitySetName": "bcbs_groupconfigurationsnapshots",
        "fetchXML": `
                <fetch version="1.0" output-format="xml-platform" mapping="logical" distinct="false" top="200">
                    <entity name="bcbs_groupconfigurationsnapshot">
                        <attribute name="bcbs_groupconfigurationsnapshotid"/>
                        <attribute name="bcbs_name"/>
                        <attribute name="createdon"/>
                        <attribute name="statecode"/>
                        <attribute name="bcbs_effectivedate"/>
                        <attribute name="statuscode"/>
                        <attribute name="bcbs_iseffectivedategoldstandard"/>
                        <attribute name="bcbs_changetypecode"/>
                        <order attribute="bcbs_effectivedate" descending="true"/>
                        <order attribute="createdon" descending="true"/>
                        <order attribute="bcbs_name" descending="false"/>
                        <filter type="and">
                            <condition attribute="bcbs_groupid" operator="eq" value="{SEARCHID}"/>
                            <condition attribute="bcbs_name" operator="like" value="%SEARCHVALUE%" />
                            @ConditionAdditional
                        </filter>
                    </entity>
                </fetch>`,
        "layout": {
            "Name": "resultset",
            "Object": 10561,
            "Rows": [
                {
                    "Name": "result",
                    "Id": "bcbs_groupconfigurationsnapshotid",
                    "Cells": [
                        {
                            "Name": "bcbs_name",
                            "Width": 300,
                            "RelatedEntityName": "",
                            "DisableMetaDataBinding": false,
                            "LabelId": "",
                            "IsHidden": false,
                            "DisableSorting": false,
                            "AddedBy": "",
                            "Desc": "",
                            "CellType": "",
                            "ImageProviderWebresource": "",
                            "ImageProviderFunctionName": "",
                            "DisplayName": "Name",
                            "IsPrimaryId": false,
                            "AttributeType": "String",
                            "IsPrimaryName": true
                        },
                        {
                            "Name": "createdon",
                            "Width": 150,
                            "RelatedEntityName": "",
                            "DisableMetaDataBinding": false,
                            "LabelId": "",
                            "IsHidden": false,
                            "DisableSorting": false,
                            "AddedBy": "",
                            "Desc": "",
                            "CellType": "",
                            "ImageProviderWebresource": "",
                            "ImageProviderFunctionName": "",
                            "DisplayName": "Created On",
                            "IsPrimaryId": false,
                            "AttributeType": "DateTime",
                            "Format": "MM/dd/yyyy, hh:mm:ss ET",
                            "IsPrimaryName": false
                        },
                        {
                            "Name": "bcbs_effectivedate",
                            "Width": 110,
                            "RelatedEntityName": "",
                            "DisableMetaDataBinding": false,
                            "LabelId": "",
                            "IsHidden": false,
                            "DisableSorting": false,
                            "AddedBy": "",
                            "Desc": "",
                            "CellType": "",
                            "ImageProviderWebresource": "",
                            "ImageProviderFunctionName": "",
                            "DisplayName": "Effective Date",
                            "IsPrimaryId": false,
                            "AttributeType": "DateTime",
                            "Format": "MM/dd/yyyy",
                            "IsPrimaryName": false
                        },
                        {
                            "Name": "statuscode",
                            "Width": 110,
                            "RelatedEntityName": "",
                            "DisableMetaDataBinding": false,
                            "LabelId": "",
                            "IsHidden": false,
                            "DisableSorting": false,
                            "AddedBy": "",
                            "Desc": "",
                            "CellType": "",
                            "ImageProviderWebresource": "",
                            "ImageProviderFunctionName": "",
                            "DisplayName": "Status Reason",
                            "IsPrimaryId": false,
                            "AttributeType": "Status",
                            "IsPrimaryName": false
                        },
                        {
                            "Name": "bcbs_changetypecode",
                            "Width": 120,
                            "RelatedEntityName": "",
                            "DisableMetaDataBinding": false,
                            "LabelId": "",
                            "IsHidden": false,
                            "DisableSorting": false,
                            "AddedBy": "",
                            "Desc": "",
                            "CellType": "",
                            "ImageProviderWebresource": "",
                            "ImageProviderFunctionName": "",
                            "DisplayName": "Change Type",
                            "IsPrimaryId": false,
                            "AttributeType": "Lookup",
                            "IsPrimaryName": true
                        },
                        {
                            "Name": "bcbs_iseffectivedategoldstandard",
                            "Width": 150,
                            "RelatedEntityName": "",
                            "DisableMetaDataBinding": false,
                            "LabelId": "",
                            "IsHidden": false,
                            "DisableSorting": false,
                            "AddedBy": "",
                            "Desc": "",
                            "CellType": "",
                            "ImageProviderWebresource": "",
                            "ImageProviderFunctionName": "",
                            "DisplayName": "Is Effective Date",
                            "IsPrimaryId": false,
                            "AttributeType": "Boolean",
                            "IsPrimaryName": false
                        }
                    ],
                    "MultiObjectIdField": "",
                    "LayoutStyle": ""
                }
            ],
            "CustomControlDescriptions": [],
            "Jump": "bcbs_name",
            "Select": true,
            "Icon": true,
            "Preview": true,
            "IconRenderer": ""
        }
    }), []);


    // https://dev.azure.com/blue-infosys/Group%20Library/_workitems/edit/391854
    const searchSnapshotQueryCriteria = useMemo(() => {

        const searchSnapshotQueryCriteria: Record<TViewKey, string> = {

            // statuscode  statuscodename                statecodename  statecode
            // 1           In Progress                   Active         0
            // 2           Underwriting Review Complete  Inactive       1
            // 808900000   Data Entry Complete           Active         0
            // 808900001   Retired                       Inactive       1
            // 808900002   Inactive                      Inactive       1
            // 808900004   MNIL Underwriting Complete    Inactive       1
            // 808900006   Gold Standard                 Inactive       1
            // 808900010   Merged and Closed             Inactive       1
            // 808900011   Cancelled                     Inactive       1
            // 808900012   Transferred to Other LOB      Inactive       1
            // 808900013   Cancellation in Progress      Active         0
            // 808900015   Pending Power User Review     Active         0


            effectiveDateGoldStandardView: `
                <condition attribute="statuscode" operator="not-in">
                    <value>808900002</value>
                    <value>808900010</value>
                </condition>
                <filter type="or">
                    <condition attribute="bcbs_iseffectivedategoldstandard" operator="eq" value="1"/>
                    <condition attribute="statuscode" operator="eq" value="808900012"/>
                    <condition attribute="bcbs_iscurrentcancelledversion" operator="eq" value="1"/>
                </filter>
            `,

            expandedView: `
                <condition attribute="statuscode" operator="not-in">
                    <value>808900010</value>
                    <value>808900002</value>
                </condition>
                <condition attribute="bcbs_blobversion" operator="not-null"/>
            `,

            inProgressAndGoldStandardView: `
            <condition attribute="statuscode" operator="in">
                <value>1</value>
                <value>808900006</value>
            </condition>
            `,

            associationTemplateView: `
            <condition attribute="bcbs_iseffectivedategoldstandard" operator="eq" value="1"/>
            `,

            inactiveView: `
            <condition attribute="statuscode" operator="in">
                <value>808900002</value>
            </condition>
            `,

            allView: ``,

            blankView: ``,

        };

        return searchSnapshotQueryCriteria;
    }, []);

    const [queryMenuSelectedKey, setQueryMenuSelectedKey] = useState<TViewKey | null>(null);

    const onPick = (snapshotRef: FAM_IEntityReferenceValue, version?: string) => {

        // invoke the callback with the selected snapshot reference and version if/after version is provided
        if (props.searchVersion && version) {
            props.onPick?.(snapshotRef, version);
        } else {
            props.onPick?.(snapshotRef, version);
        }

    }

    const searchSnapshotQuery = useMemo(() => {

        if (!searchAccount)
            return null;

        if (!queryMenuSelectedKey)
            return null;

        let cloned = structuredClone(viewSearchSnapshot());
        cloned.fetchXML = cloned.fetchXML.replace('SEARCHID', searchAccount.id);

        // https://dev.azure.com/blue-infosys/Group%20Library/_workitems/edit/431757

        // Lookup Query Updates
        // https://dev.azure.com/blue-infosys/Group%20Library/_workitems/edit/391854
        if (queryMenuSelectedKey === 'expandedView') {
            cloned.fetchXML = cloned.fetchXML.replace('@ConditionAdditional', searchSnapshotQueryCriteria.expandedView);
        } else if (queryMenuSelectedKey === 'inactiveView') {
            cloned.fetchXML = cloned.fetchXML.replace('@ConditionAdditional', searchSnapshotQueryCriteria.inactiveView);
        } else if (queryMenuSelectedKey === 'effectiveDateGoldStandardView') {
            cloned.fetchXML = cloned.fetchXML.replace('@ConditionAdditional', searchSnapshotQueryCriteria.effectiveDateGoldStandardView);
        } else if (queryMenuSelectedKey === 'inProgressAndGoldStandardView') {
            cloned.fetchXML = cloned.fetchXML.replace('@ConditionAdditional', searchSnapshotQueryCriteria.inProgressAndGoldStandardView);
        } else if (queryMenuSelectedKey === 'associationTemplateView') {
            cloned.fetchXML = cloned.fetchXML.replace('@ConditionAdditional', searchSnapshotQueryCriteria.associationTemplateView);
        } else if (queryMenuSelectedKey === 'allView') {
            cloned.fetchXML = cloned.fetchXML.replace('@ConditionAdditional', searchSnapshotQueryCriteria.allView);
        }

        return JSON.stringify(cloned);

    }, [queryMenuSelectedKey, searchAccount])

    useEffect(() => {
        let defaultQueryMenuSelectedKey: TViewKey = 'effectiveDateGoldStandardView';

        if (props.defaultValueViewKey && queryMenuSelectedKey !== props.defaultValueViewKey) {
            defaultQueryMenuSelectedKey = props.defaultValueViewKey;
        }

        setQueryMenuSelectedKey(defaultQueryMenuSelectedKey);
    }, [])

    const onCancel = () => {
        setSearching(false);
        props.onCancel?.();
    }

    /**
     * Renders an information callout with audit data.
     * @param auditData - The audit data to display in the callout.
     * @returns The rendered InformationCallout component.
     */
    const onRenderInformationCallout = (auditData?: IAuditData) => {

        if (!auditData)
            return <></>;

        return <InformationCallout
            hover
            postText={auditData.compareKeys}
            directionalHint={DirectionalHint.rightCenter}
            calloutClassName={appStyles.calloutUltraWide}
        >
            <div style={{ width: '100%' }}>
                <table>
                    <thead>
                        <tr>
                            <th style={{ textAlign: 'left', borderBottom: '1px solid gray' }}>
                                <Text>
                                    Field
                                </Text>
                            </th>
                            <th style={{ textAlign: 'left', borderLeft: '1px solid gray', borderBottom: '1px solid gray' }}>
                                <Text>
                                    Old Value
                                </Text>
                            </th>
                            <th style={{ textAlign: 'left', borderLeft: '1px solid gray', borderBottom: '1px solid gray' }}>
                                <Text>
                                    New Value
                                </Text>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {Object.entries(auditData.compare).map(([key, value], index) => (
                            <tr key={key} style={{ backgroundColor: index % 2 === 0 ? theme.semanticColors.bodyBackground : undefined }}>
                                <td style={{ overflow: 'hidden', width: '150px' }}>
                                    <Text variant="small">{key}</Text>
                                </td>
                                <td style={{ overflow: 'hidden', width: '200px', borderLeft: '1px solid gray' }}>
                                    <Text variant="small">{typeof value?.oldAudit?.value === 'string' ? value?.oldAudit?.value : ""}</Text>
                                </td>
                                <td style={{ overflow: 'hidden', width: '200px', borderLeft: '1px solid gray' }}>
                                    <Text variant="small">{typeof value?.newAudit?.value === 'string' ? value?.newAudit?.value : ""}</Text>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </InformationCallout>;
    }

    /**
     * Searches for snapshot versions based on the provided value.
     * @param value - The search value.
     * @param updateStatus - Optional status delegate function.
     * @returns A promise that resolves to an object containing columns, items, and prompts for displaying snapshot versions.
     */
    const onSearchSnapshotVersions: TSearchPredicateAsync<IScopeSearchResult> = async (value, updateStatus) => {
        const searchValue = value.toLowerCase() ?? '';

        return new Promise(async (resolve, reject) => {

            const mergeAuditRecords = (oldAttributes: TRecordIAuditAttribute, newAttributes: TRecordIAuditAttribute) => {

                const merged: TCompareAuditAttribute = {};

                Object.keys(oldAttributes).forEach((eachKey) => {
                    merged[eachKey] = {
                        ...merged[eachKey],
                        oldAudit: oldAttributes[eachKey],
                    }
                });

                Object.keys(newAttributes).forEach((eachKey) => {
                    merged[eachKey] = {
                        ...merged[eachKey],
                        newAudit: newAttributes[eachKey],
                    }
                });

                return merged;
            }

            try {


                let items: IGridItem<IScopeSearchResult>[] = [];

                updateStatus?.("Loading blob versions/configuration audits...");
                const snapshotVersions = await dataSource.getSnapshotVersions(state.data.snapshot.bcbs_filename!);

                const snapshotAudits = await dataSource.getHttpStartGetSnapshotAudit(state.data.snapshot.bcbs_groupconfigurationsnapshotid!);

                const audits: Record<string, IAuditData> = {};
                snapshotAudits.auditDetails.forEach((eachAuditDetail) => {

                    // if backend doesn't provide these, set them to empty arrays
                    eachAuditDetail.auditRecord = eachAuditDetail.auditRecord ?? { attributes: [], formattedValues: [] };
                    eachAuditDetail.newValue = eachAuditDetail.newValue ?? { attributes: [], formattedValues: [] };
                    eachAuditDetail.oldValue = eachAuditDetail.oldValue ?? { attributes: [], formattedValues: [] };

                    const auditRecordAttributesLookup = ArrayToLookupByPredicate(
                        eachAuditDetail.auditRecord.attributes,
                        (eachAttribute) => eachAttribute.key
                    );

                    let newValuedAttributesLookup = ArrayToLookupByPredicate(
                        eachAuditDetail.newValue.attributes,
                        (eachAttribute) => eachAttribute.key
                    );

                    const newValuedAttributesFormattedValuesLookup = ArrayToLookupByPredicate(
                        eachAuditDetail.newValue.formattedValues,
                        (eachAttribute) => eachAttribute.key
                    );

                    let oldValuedAttributesLookup = ArrayToLookupByPredicate(
                        eachAuditDetail.oldValue.attributes,
                        (eachAttribute) => eachAttribute.key
                    );

                    const oldValuedAttributesFormattedValuesLookup = ArrayToLookupByPredicate(
                        eachAuditDetail.oldValue.formattedValues,
                        (eachAttribute) => eachAttribute.key
                    );

                    newValuedAttributesLookup = {
                        ...newValuedAttributesLookup,
                        ...newValuedAttributesFormattedValuesLookup
                    };

                    oldValuedAttributesLookup = {
                        ...oldValuedAttributesLookup,
                        ...oldValuedAttributesFormattedValuesLookup
                    }

                    const compare = mergeAuditRecords(oldValuedAttributesLookup, newValuedAttributesLookup);

                    const user = auditRecordAttributesLookup['userid']?.value as IAuditFieldValueUserID;
                    const bcbs_blobversion = newValuedAttributesLookup['bcbs_blobversion']?.value as string | undefined;

                    if (bcbs_blobversion)
                        audits[bcbs_blobversion.replace("V", "")] = {
                            data: eachAuditDetail,
                            user: user,
                            compare: compare,
                            compareKeys: Object.keys(compare).join(', '),
                        };
                })

                snapshotVersions.forEach((eachVersion) => {

                    const name = Formatters.ToShortDateTimeAmericaNewYork(eachVersion)!;

                    const audit = audits[eachVersion];

                    items.push({
                        key: eachVersion,
                        _index: 1,
                        search: eachVersion,
                        name: name,
                        user: audit?.user?.name,
                        data: {
                            id: eachVersion!,
                            name: name,
                            version: eachVersion,
                            audit: audit,
                        }
                    })
                });

                resolve({
                    columns: [
                        {
                            key: 'name',
                            minWidth: 150,
                            name: 'Version',
                            fieldName: 'name',
                            isResizable: true,
                        },
                        {
                            key: 'user',
                            minWidth: 150,
                            name: 'User',
                            fieldName: 'user',
                            isResizable: true,
                        },
                        {
                            key: 'changes',
                            minWidth: 400,
                            name: 'Changes',
                            fieldName: 'Changes',
                            isResizable: true,
                            onRender(item: IGridItem<IScopeSearchResult>, index, column) {
                                return onRenderInformationCallout(item?.data?.audit)
                            },
                        },
                    ],
                    items: items,
                    showNoResultPrompt: items.length === 0,
                    showLimitedResultPrompt: false,
                });

            } catch (error) {
                reject(error);
            } finally {
            }
        });
    }

    interface IContextualMenuItemTKey extends IContextualMenuItem {
        key: TViewKey;
    }

    const queryMenuItems = useMemo(() => {
        const queryMenuItems: IContextualMenuItemTKey[] = [
            {
                key: 'effectiveDateGoldStandardView',
                text: 'Effective Date Gold Standard View - Sort by Effective Date/Create Date',
                onClick: () => {
                    setQueryMenuSelectedKey('effectiveDateGoldStandardView');
                }
            },
            {

                key: 'expandedView',
                text: 'Expanded View - Sort by Effective Date/Create Date',
                onClick: () => {
                    setQueryMenuSelectedKey('expandedView');
                }
            },
            {

                key: 'inProgressAndGoldStandardView',
                text: 'In-Progress/Gold Standard View - Sort by Effective Date/Create Date',
                onClick: () => {
                    setQueryMenuSelectedKey('inProgressAndGoldStandardView');
                }
            },
            {
                key: 'allView',
                text: 'All View - Sort by Effective Date/Create Date',
                onClick: () => {
                    setQueryMenuSelectedKey('allView');
                }
            },
            {
                key: 'inactiveView',
                text: 'Inactive View - Sort by Effective Date/Create Date',
                onClick: () => {
                    setQueryMenuSelectedKey('inactiveView');
                }
            },
        ];
        return queryMenuItems;
    }, []);


    const reset = () => {
        setSearchAccount(props.valueAccount ?? null);
        setSearchSnapshot(props.valueSnapshot ?? null);
        setSearchSnapshotVersion(props.valueSnapshotVersion ?? null);
        setSearching(true);
    }

    return <>
        <LookupControlBase
            label={props.label ?? 'Snapshot'}
            valueEntityReference={searchSnapshot ?? undefined}
            // onPreview={() => {
            //     setIsOpenModal(true);
            // }}
            description={props.searchVersion
                ? `Version : ${searchSnapshotVersion ?? '---'}`
                : undefined
            }
            disabled={props.disabled}
            onLookup={() => {
                reset();
            }}
        />


        <LookupControlDialog
            isOpen={
                searching &&
                searchAccount === null
            }
            label={'BlueRelationship'}
            variant={'single'}
            masterAttribute={{
                bcbs_lookupviewdefinition: JSON.stringify(viewSearchBlueRelationship())
            }}
            onPick={(event) => {
                if (event?.length === 1) {
                    setSearchAccount(event[0] as FAM_IEntityReferenceValue);
                }
            }}
            onCancel={() => {
                setSearching(false);
                props.onCancel?.();
            }}
            onDismiss={() => {
                setSearching(false);
                props.onCancel?.();
            }}
        />

        <LookupControlDialog
            isOpen={
                searching &&
                searchSnapshotQuery !== null &&
                searchAccount !== null &&
                searchSnapshot === null
            }
            label={'Snapshot'}
            variant={'single'}
            masterAttribute={{
                bcbs_lookupviewdefinition: searchSnapshotQuery!
            }}
            // transformer={(eachLine) => {
            //     return eachLine;
            // }}
            onPick={(event) => {
                if (event?.length === 1) {
                    const snapshot = event[0] as FAM_IEntityReferenceValue;
                    setSearchSnapshot(snapshot);
                    onPick(snapshot);
                }
            }}
            onCancel={onCancel}
            onDismiss={onCancel}
            queryMenuItems={queryMenuItems}
            queryMenuSelectedKey={queryMenuSelectedKey ?? undefined}
        />

        {props.searchVersion &&
            <LookupControlDialog
                isOpen={
                    searching &&
                    searchSnapshotQuery !== null &&
                    searchAccount !== null &&
                    searchSnapshot !== null &&
                    searchSnapshotVersion === null
                }
                label={'Version'}
                variant={'single'}
                onSearch={onSearchSnapshotVersions}
                onPick={(event) => {
                    if (event?.length === 1) {
                        setSearchSnapshotVersion(event[0].version);
                        onPick(searchSnapshot!, event[0].version);
                    }
                }}
                onCancel={onCancel}
                onDismiss={onCancel}
                hideSearch={true}
            />
        }


        {isOpenModal &&
            <Modal isOpen={true}>
                <div style={{ position: 'fixed', top: 0, right: 0 }}>
                    <IconButton
                        styles={{
                            root: {
                                backgroundColor: 'white',
                            }
                        }}
                        iconProps={{ iconName: 'Cancel' }}
                        title="Close"
                        ariaLabel="Close"
                        onClick={() => {
                            setIsOpenModal(false);
                        }}
                    />
                </div>
                <iframe style={{ width: '95vw', height: '95vh', border: 'none' }}
                    src={AppLinkToExternal({ type: 'groupLibrary', id: [searchAccount?.id!, searchSnapshot?.id!] })}
                >
                </iframe>
            </Modal>
        }

    </>
}
export default SnapshotLookup;